import React, { useState, useEffect } from 'react';
import { landlordAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Star, TrendingUp, MessageSquare, Wrench, DollarSign, Award } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Progress } from '../../components/ui/progress';

function StarDisplay({ rating, size = 'md' }) {
  const sizeClass = size === 'sm' ? 'w-4 h-4' : size === 'md' ? 'w-5 h-5' : 'w-6 h-6';
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={cn(
            sizeClass,
            rating >= star ? 'fill-yellow-400 text-yellow-400' : 'fill-transparent text-muted-foreground'
          )}
        />
      ))}
    </div>
  );
}

function CategoryRating({ icon: Icon, label, rating, color }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2">
        <Icon className={cn("w-4 h-4", color)} />
        <span className="text-sm">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <Progress value={rating * 20} className="w-24 h-2" />
        <span className="text-sm font-medium w-8">{rating.toFixed(1)}</span>
      </div>
    </div>
  );
}

export default function LandlordRatings() {
  const [ratings, setRatings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ratingsRes, summaryRes] = await Promise.all([
        landlordAPI.getMyRatings(),
        landlordAPI.getRatingsSummary()
      ]);
      setRatings(ratingsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching ratings:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading ratings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="landlord-ratings-page">
      <div>
        <h1 className="text-2xl font-bold">My Ratings</h1>
        <p className="text-muted-foreground">View feedback from your tenants</p>
      </div>

      {/* Summary Card */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-500" />
              Overall Rating
            </CardTitle>
            <CardDescription>Based on {summary?.total_ratings || 0} reviews</CardDescription>
          </CardHeader>
          <CardContent>
            {summary?.total_ratings > 0 ? (
              <div className="flex items-center gap-4">
                <div className="text-5xl font-bold">{summary?.average_overall || 0}</div>
                <div className="space-y-1">
                  <StarDisplay rating={Math.round(summary?.average_overall || 0)} size="lg" />
                  <p className="text-sm text-muted-foreground">out of 5 stars</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground">
                <p>No ratings yet</p>
                <p className="text-sm">Ratings will appear here after tenants review their experience</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Category Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.total_ratings > 0 ? (
              <div className="space-y-1">
                <CategoryRating
                  icon={MessageSquare}
                  label="Communication"
                  rating={summary?.average_categories?.communication || 0}
                  color="text-blue-500"
                />
                <CategoryRating
                  icon={TrendingUp}
                  label="Responsiveness"
                  rating={summary?.average_categories?.responsiveness || 0}
                  color="text-green-500"
                />
                <CategoryRating
                  icon={Wrench}
                  label="Property Condition"
                  rating={summary?.average_categories?.property_condition || 0}
                  color="text-orange-500"
                />
                <CategoryRating
                  icon={DollarSign}
                  label="Value for Money"
                  rating={summary?.average_categories?.value_for_money || 0}
                  color="text-purple-500"
                />
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No category data yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reviews List */}
      <Card>
        <CardHeader>
          <CardTitle>Tenant Reviews</CardTitle>
          <CardDescription>What your tenants are saying</CardDescription>
        </CardHeader>
        <CardContent>
          {ratings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No reviews yet</p>
              <p className="text-sm">Reviews from tenants will appear here once approved by admin</p>
            </div>
          ) : (
            <div className="space-y-4">
              {ratings.map((rating) => (
                <div
                  key={rating.rating_id}
                  className="border rounded-lg p-4 space-y-3"
                  data-testid={`review-${rating.rating_id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <StarDisplay rating={rating.overall_rating} />
                        <span className="font-medium">{rating.overall_rating}/5</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        By {rating.tenant?.name || 'Anonymous Tenant'}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(rating.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  {/* Category Pills */}
                  <div className="flex flex-wrap gap-2">
                    <div className="flex items-center gap-1 text-xs bg-muted px-2 py-1 rounded">
                      <MessageSquare className="w-3 h-3" />
                      Communication: {rating.categories?.communication || 0}
                    </div>
                    <div className="flex items-center gap-1 text-xs bg-muted px-2 py-1 rounded">
                      <TrendingUp className="w-3 h-3" />
                      Responsiveness: {rating.categories?.responsiveness || 0}
                    </div>
                    <div className="flex items-center gap-1 text-xs bg-muted px-2 py-1 rounded">
                      <Wrench className="w-3 h-3" />
                      Property: {rating.categories?.property_condition || 0}
                    </div>
                    <div className="flex items-center gap-1 text-xs bg-muted px-2 py-1 rounded">
                      <DollarSign className="w-3 h-3" />
                      Value: {rating.categories?.value_for_money || 0}
                    </div>
                  </div>

                  {/* Review Text */}
                  {rating.review_text && (
                    <div className="bg-muted/50 rounded p-3">
                      <p className="text-sm italic">"{rating.review_text}"</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
